/* eslint-disable jsx-a11y/anchor-is-valid */
import classNames from 'classnames'
import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import { EMAIL_REGEX } from '../../../util/Regex'
import { sortAlphabetically } from '../../../util/Functions'

interface Props {
  id: string
  title: string
  description: string
  toListContacts?: string[]
  icon?: string
  submitOnClickHandler: (toList: string[], comments?: string) => void
}

const ShareConfirmationModal: FC<Props> = ({
  id,
  title,
  description,
  toListContacts,
  icon = 'bi-envelope',
  submitOnClickHandler,
}) => {
  const intl = useIntl()
  const iconClass = `fs-1 text-primary ${icon}`

  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isDefaultContact, setIsDefaultContact] = useState(false)
  const [toListInput, setToListInput] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isCommentsEnabled, setIsCommentsEnabled] = useState(false)
  const [commentsValue, setCommentsValue] = useState('')

  useEffect(() => {
    if (isDefaultContact && toListContacts) {
      setToListInput([...toListContacts, ...toListInput].sort(sortAlphabetically))
    } else {
      setToListInput(
        toListInput.filter((contact) => !toListContacts?.includes(contact)).sort(sortAlphabetically)
      )
    }
  }, [isDefaultContact])

  const commentsOnChangeHandler = (event) => {
    setCommentsValue(event.target.value)
  }

  const searchBarOnChangeHandler = (event) => {
    setInputValue(event.target.value)
  }

  const searchBarOnKeyDownHandler = (e) => {
    if (e.key !== 'Enter' || !e.target.value || !e.target.value.trim()) {
      setIsValidEmail(true)
      return
    }

    setInputValue(e.target.value)
    addOnClickHandler()
  }

  const itemRemoveOnClickHandler = (index) => {
    if (toListInput.length > 0) {
      setToListInput(toListInput.filter((el, i) => i !== index).sort(sortAlphabetically))
    }
  }

  const checkboxOnChange = (isChecked: boolean) => {
    setIsDefaultContact(isChecked)
  }

  const addOnClickHandler = () => {
    if (!inputValue || !inputValue.trim()) {
      setIsValidEmail(true)
      return
    }

    const rawEmailList = inputValue.trim().split(',')
    const validEmailList = rawEmailList.filter((email) => email && email.trim().match(EMAIL_REGEX))
    const invalidEmailList = rawEmailList.filter(
      (email) => email && !email.trim().match(EMAIL_REGEX)
    )

    setToListInput([...toListInput, ...validEmailList].sort(sortAlphabetically))

    if (invalidEmailList.length > 0) {
      const invalidInputValue = invalidEmailList.join(', ')
      setInputValue(invalidInputValue)
      setIsValidEmail(false)
    } else {
      setInputValue('')
      setIsValidEmail(true)
    }
  }

  const onClickHandler = () => {
    const recipients = [...toListInput];
    const comments = commentsValue;
    setToListInput([]);
    setCommentsValue('')
    setIsDefaultContact(false)
    setIsCommentsEnabled(false)
    submitOnClickHandler(recipients, comments)
  }

  return (
    <div className='modal fade' tabIndex={-1} id={id} data-bs-backdrop='static'>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header border-0 pb-0'>
            <div className='d-flex flex-wrap align-items-center justify-content-start'>
              <div className='d-flex flex-column flex-row-fluid'>
                <h5 className='modal-title'>{title}</h5>
              </div>
            </div>
          </div>

          <div className='modal-body mt-0 pt-3'>
            <div className='separator separator-dashed border-primary opacity-25 mt-2 mb-5'></div>

            <div className='mt-5'>
              {intl.formatMessage({id: 'UTIL.SHARE.CONFIRMATION.INSTRUCTIONS_1'})}
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-end'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='input-tag me-2'>
                  <ul className='input-tag-items bg-light-primary ps-3 py-0'>
                    <li className='tag-input'>
                      <input
                        style={{
                          textDecoration: isValidEmail ? 'none' : 'underline',
                          textDecorationColor: isValidEmail ? 'none' : 'red',
                        }}
                        className='bg-light-primary'
                        type='text'
                        placeholder={intl.formatMessage({id: 'UTIL.EMAIL'})}
                        disabled={!enabled || loading}
                        onKeyDown={searchBarOnKeyDownHandler}
                        onChange={searchBarOnChangeHandler}
                        value={inputValue}
                      />
                    </li>
                  </ul>
                </div>
              </div>
              <div className='d-flex flex-column-auto flex-center'>
                <div className='d-flex flex-row flex-column-fluid py-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => addOnClickHandler()}
                  >
                    <span className='indicator-label'>{intl.formatMessage({id: 'UTIL.ADD'})}</span>
                  </button>
                </div>
              </div>
            </div>

            {toListContacts && toListContacts.length > 0 && (
              <div className='form-check form-switch form-check-custom form-check-solid mt-5 ms-3'>
                <input
                  className='form-check-input h-20px w-30px'
                  type='checkbox'
                  onChange={(e) => checkboxOnChange(e.target.checked)}
                />
                <label className='form-check-label'>
                  {intl.formatMessage({id: 'UTIL.SHARE.CONFIRMATION.DEFAULT_CONTACTS'})}
                </label>
              </div>
            )}

            {toListInput && toListInput.length > 0 && (
              <div>
                <div className='mt-5'>
                  {intl.formatMessage({id: 'UTIL.SHARE.CONFIRMATION.INSTRUCTIONS_2'})}
                </div>

                <div className='d-flex flex-row flex-column-fluid mt-1 align-items-center mt-5 mb-3'>
                  <div className='d-flex flex-column flex-row-fluid'>
                    <div className='input-tag bg-white'>
                      <ul className='input-tag-transparent p-0 ms-3'>
                        {toListInput.map((filter, index) => (
                          <div className={classNames('badge bg-secondary m-1 py-1')} key={index}>
                            <div className='d-flex align-items-center'>
                              <i
                                className='bi bi-x fs-1 ms-n2 fw-bold text-danger cursor-pointer text-hover-danger'
                                onClick={() => itemRemoveOnClickHandler(index)}
                              ></i>
                              <span className='text-primary fs-7'>{filter.toUpperCase()}</span>
                            </div>
                          </div>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isCommentsEnabled && (
              <div className='mt-10'>
                ¿{intl.formatMessage({id: 'UTIL.WOULD_YOU_LIKE_TO'})}{' '}
                <strong>
                  <a
                    className='text-primary cursor-pointer'
                    onClick={() => setIsCommentsEnabled(!isCommentsEnabled)}
                  >
                    {intl.formatMessage({id: 'UTIL.ADD'})}
                  </a>
                </strong>{' '}
                {intl.formatMessage({id: 'UTIL.ADDITIONAL_COMMENTS'})}?
              </div>
            )}

            {isCommentsEnabled && (
              <>
                <div className='d-flex flex-row flex-column-fluid mt-1 align-items-center mt-5 mb-3 ms-3 me-3'>
                  <div className='d-flex flex-column flex-row-fluid'>
                    <textarea
                      rows={3}
                      className='form-control form-control-lg form-control-solid px-5 py-5 fs-6 text-dark'
                      placeholder={`${intl.formatMessage({id: 'UTIL.ADDITIONAL_COMMENTS'})}...`}
                      onChange={commentsOnChangeHandler}
                      disabled={!enabled || loading}
                      value={commentsValue}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='modal-footer border-0'>
            <button type='button' className='btn btn-sm btn-light' data-bs-dismiss='modal'>
              {intl.formatMessage({id: 'UTIL.CLOSE'})}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary btn-active-color-secondary me-2'
              data-bs-dismiss='modal'
              onClick={onClickHandler}
              disabled={!isValidEmail || toListInput.length === 0}
            >
              {intl.formatMessage({id: 'SUBMIT'})}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export {ShareConfirmationModal}
